import {
  ChartData,
  Plugin,
  ILineChartEventListeners,
  ILineChartZoomManager,
  ILineChartPaddings,
} from '@/shared/Charting/LineChart/lineChart.types.ts'
import lineChartZoomManager from '@/shared/Charting/LineChart/helpers/LineChartZoomManager.ts'

/**
 * Линейный график https://www.figma.com/design/Wx0J0AW2tWUm7wmNQ8fvzt/%D0%98%D0%BD%D1%82%D0%B5%D1%80%D1%82%D0%B5%D1%85?node-id=5279-41544&t=dRoruHoDH4rRRhhB-4
 *
 * Использует canvas для отрисовки. Реализован на системе плагинов, сам класс LineChart обеспечивает жизненный цикл графика, хранит состояние и предрсиавояет методы работы с осями.
 * */
class LineChart {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
  data: ChartData
  paddings: ILineChartPaddings = {
    left: 0,
    top: 0,
    bottom: 60,
    right: 60,
  }
  padding: number = 60
  pluginsInnerPaddings: Array<{ pluginName: string; paddings: ILineChartPaddings }> = []
  innerPadding: ILineChartPaddings = { bottom: 0, top: 0, right: 0, left: 0 }
  plugins: Plugin[] = []

  zoom: ILineChartZoomManager
  listeners: ILineChartEventListeners = {} as any

  constructor(canvas: HTMLCanvasElement, chartData?: ChartData) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')!
    this.data = {
      datasets: chartData?.datasets || [],
      xAxesConf: chartData?.xAxesConf || { type: 'time', tsTo: Date.now(), tsFrom: Date.now() - 1000 * 60 },
    }

    this.zoom = lineChartZoomManager()

    this.initEvents()
    this.render()
  }

  use(plugin: Plugin) {
    plugin.init(this)
    this.plugins.push(plugin)

    this.render()
  }

  initEvents() {
    this.listeners = {
      mousemove: (e) => this.plugins.forEach((p) => p.onMouseMove?.(e)),
      mousedown: (e) => this.plugins.forEach((p) => p.onMouseDown?.(e)),
      mouseup: (e) => this.plugins.forEach((p) => p.onMouseUp?.(e)),
      mouseleave: (e) => this.plugins.forEach((p) => p.onMouseLeave?.(e)),
      wheel: (e) => this.plugins.forEach((p) => p.onWheel?.(e)),
    }

    this.canvas.addEventListener('mousemove', this.listeners.mousemove)
    this.canvas.addEventListener('mousedown', this.listeners.mousedown)
    this.canvas.addEventListener('mouseup', this.listeners.mouseup)
    this.canvas.addEventListener('mouseleave', this.listeners.mouseleave)
    this.canvas.addEventListener('wheel', this.listeners.wheel)
  }

  getX(index: number): number {
    const totalOxPadding = this.paddings.left + this.paddings.right

    const range = this.zoom.xMax - this.zoom.xMin
    return this.paddings.left + ((index - this.zoom.xMin) / range) * (this.canvas.width - totalOxPadding)
  }

  getXValue(xCoordinate: number): number {
    const totalOxPadding = this.paddings.left + this.paddings.right

    const range = this.zoom.xMax - this.zoom.xMin
    const value = (range * (xCoordinate - this.paddings.left)) / (this.canvas.width - totalOxPadding) + this.zoom.xMin

    return Math.round(value)
  }

  getY(value: number): number {
    const totalOyPadding = this.paddings.top + this.paddings.bottom

    const range = this.zoom.yMax - this.zoom.yMin
    const chartHeight = this.canvas.height - totalOyPadding - this.innerPadding.bottom - this.innerPadding.top
    const startYCoord = this.paddings.top + this.innerPadding.top

    return startYCoord + ((this.zoom.yMax - value) / range) * chartHeight
  }

  getYValue(yCoordinate: number): number {
    const totalOyPadding = this.paddings.top + this.paddings.bottom

    const range = this.zoom.yMax - this.zoom.yMin
    const startYCoord = this.paddings.top + this.innerPadding.top
    const chartHeight = this.canvas.height - totalOyPadding - this.innerPadding.bottom - this.innerPadding.top

    return this.zoom.yMax - ((yCoordinate - startYCoord) * range) / chartHeight
  }

  clearOutsideTheChart() {
    // Отчистка пространстрванства вне графика (в районе padding)
    this.ctx.clearRect(0, 0, this.paddings.left, this.canvas.height)
    this.ctx.clearRect(this.canvas.width - this.paddings.right + 1, 0, this.padding, this.canvas.height)
    this.ctx.clearRect(0, 0, this.canvas.width, this.paddings.top)
    this.ctx.clearRect(0, this.canvas.height - this.paddings.bottom, this.canvas.width, this.paddings.bottom)
  }

  setPluginInnerPaddings(pluginName: string, paddings: ILineChartPaddings) {
    const paddingsIndex = this.pluginsInnerPaddings.findIndex((p) => p.pluginName === pluginName)
    if (paddingsIndex === -1) {
      this.pluginsInnerPaddings.push({ pluginName, paddings })
    } else {
      this.pluginsInnerPaddings[paddingsIndex] = { pluginName, paddings }
    }

    const resultingInnerPaddings: ILineChartPaddings = {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    }

    for (const pluginPadding of this.pluginsInnerPaddings) {
      for (const key in pluginPadding.paddings) {
        resultingInnerPaddings[key] += pluginPadding.paddings[key]
      }
    }

    this.innerPadding = resultingInnerPaddings
  }

  render() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    this.plugins.forEach((p) => p.onBeforeRender?.(this.ctx))

    // Draw borders
    this.ctx.strokeStyle = '#0000001A'
    this.ctx.lineWidth = 1
    this.ctx.beginPath()
    this.ctx.moveTo(this.paddings.left, this.paddings.top)
    this.ctx.lineTo(this.paddings.left, this.canvas.height - this.paddings.bottom)

    this.ctx.moveTo(this.paddings.left, this.canvas.height - this.paddings.bottom)
    this.ctx.lineTo(this.canvas.width - this.paddings.right, this.canvas.height - this.paddings.bottom)

    this.ctx.moveTo(this.canvas.width - this.paddings.right, this.canvas.height - this.paddings.bottom)
    this.ctx.lineTo(this.canvas.width - this.paddings.right, this.paddings.top)

    this.ctx.moveTo(this.canvas.width - this.paddings.right, this.paddings.top)
    this.ctx.lineTo(this.paddings.left, this.paddings.top)
    this.ctx.stroke()
    this.ctx.closePath()

    // Render data
    this.plugins.forEach((p) => p.render?.(this.ctx))
    this.clearOutsideTheChart()

    // Дорисовка границ в правом нижнем углу согласно дизайну
    this.ctx.beginPath()
    this.ctx.strokeStyle = '#0000001A'
    this.ctx.lineWidth = 1
    this.ctx.moveTo(this.canvas.width - this.paddings.right, this.canvas.height - this.paddings.bottom / 2)
    this.ctx.lineTo(this.canvas.width - this.paddings.right, this.canvas.height - this.paddings.bottom)
    this.ctx.lineTo(this.canvas.width, this.canvas.height - this.paddings.bottom)

    this.ctx.stroke()
    this.ctx.closePath()

    this.plugins.forEach((p) => p.onAfterRender?.(this.ctx))

    // Чистим правый нижний угол
    this.ctx.clearRect(
      this.canvas.width - this.paddings.right + 1,
      this.canvas.height - this.paddings.bottom,
      this.paddings.right,
      this.paddings.bottom
    )
  }

  destroy() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.canvas.removeEventListener('mousemove', this.listeners.mousemove)
    this.canvas.removeEventListener('mousedown', this.listeners.mousedown)
    this.canvas.removeEventListener('mouseup', this.listeners.mouseup)
    this.canvas.removeEventListener('mouseleave', this.listeners.mouseleave)
    this.canvas.removeEventListener('wheel', this.listeners.wheel)
  }
}

export default LineChart
